import { getEntityScope } from './entity-scope-read';
import { tokenClaims } from './entity-claim';
import { fetchScopeCheckRequest } from './entity-scope-api';
import { inTokenTree } from './entity-tree';
import { createBoundedRetry } from './bounded-retry';
import { getStore } from '../store/get-store';
import {
  scopeRootStamped,
  UNREACHABLE_ROOT,
  type EntityScopeState,
} from '../slice/entityScopeSlice';
import { scopeRootOfClaims } from '../util/jwt';

/**
 * Gives a scope persisted before DV-3421 the root stamp it never got (review
 * round 6, item A). Split out of `scope-reconciliation.ts` (review of
 * 291019f0, item 8); `reconcileScope` calls it for a scope with no stamp.
 *
 * <p>Local first (review of 291019f0, item 2). When this tab can show the
 * selection is inside the token's own tree (`inTokenTree`) the root is stamped
 * with no request. That is every user sitting on their home account, and it
 * keeps a deploy-day request off every open tab.
 *
 * <p>Otherwise the backend is asked (`/entities/scope/check`) once per
 * selection and root: reachable → the root; not → {@link UNREACHABLE_ROOT},
 * which then reads as a move. Only a DEFINITE outcome is remembered (review of
 * 24a2f326, item 6): a boolean answer, or a 4xx refusal. A transient failure —
 * 5xx, network, or a 2xx without a boolean (the route not deployed yet) —
 * leaves it unstamped and retries with a bounded backoff, so a tab nobody
 * touches is still stamped once the service recovers.
 *
 * <p>With `honour-selection` off the check answers yes only for the token's
 * own `EntityUUID`. Sub-entities of the token's tree are settled locally above,
 * so a flag-off rollback does not evict them (review of 0a17c730, item 3).
 */

let asked: { entityId: string; root: string } | null = null;
const retry = createBoundedRetry();

/** A 4xx: the backend refused the question itself, which asking again will not change. */
const isDefinite = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
};

/**
 * Stamp `scope` (scoped, unstamped) for `root`, the stored token's root.
 * Returns true when it was stamped synchronously; otherwise the backend has
 * been asked (or already was), and `onStamped` runs if and when it answers.
 */
export const stampUnstampedScope = (
  scope: EntityScopeState,
  root: string,
  onStamped: () => void,
): boolean => {
  const entityId = scope.entityId;
  if (entityId === null) return false;
  if (inTokenTree(scope, root, entityId)) {
    getStore().dispatch(scopeRootStamped(root));
    return true;
  }
  if (asked?.entityId === entityId && asked.root === root) return false;
  asked = { entityId, root };
  fetchScopeCheckRequest(entityId)
    .then((reachable) => {
      // Answered for the token as it was. If it has moved since, or the tab
      // has switched, the next trigger asks again.
      if (scopeRootOfClaims(tokenClaims()) !== root || getEntityScope().entityId !== entityId) {
        return;
      }
      retry.reset();
      getStore().dispatch(scopeRootStamped(reachable ? root : UNREACHABLE_ROOT));
      onStamped();
    })
    .catch((error: unknown) => {
      if (isDefinite(error)) return;
      // Transient: forget the question, and ask again after a backoff.
      asked = null;
      retry.schedule(onStamped);
    });
  return false;
};

/** Test seam. */
export const __resetStampCheckForTests = (): void => {
  asked = null;
  retry.reset();
};
